import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { toast } from "react-toastify";
import axios from "../../api/axios_client";
import './ManagementPages.css';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';

const IndexManagement = () => {
  const [indexes, setIndexes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [indexTypes, setIndexTypes] = useState([]);
  const [searchType, setSearchType] = useState('');

  const fetchIndexes = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin-panel', {
        params: { tab: 'indices' }
      });
      setIndexes(response.data);
    } catch (error) {
      toast.error('Failed to fetch indexes');
    } finally {
      setLoading(false);
    }
  };

  const fetchIndexTypes = async () => {
    try {
      const response = await axios.get('/index-types');
      setIndexTypes(response.data);
    } catch (error) {
      toast.error('Failed to fetch index types');
    }
  };

  useEffect(() => {
    fetchIndexes();
    fetchIndexTypes();
  }, []);

  const handleDelete = (index) => {
    setSelectedIndex(index);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`/indexes/${selectedIndex.id}`);
      toast.success('Index deleted successfully');
      fetchIndexes();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete index';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setSelectedIndex(null);
    }
  };

  const handleApprovalToggle = (index) => {
    // Placeholder for future implementation
    toast.info(`Approval toggle for ${index.name} - soon!`);
  };

  const filteredIndexes = indexes.filter(index => {
    if (!searchType) return true;
    return index.type?.toLowerCase() === searchType.toLowerCase();
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="management-page">
      <div className="management-header">
        <h1>Index Management</h1>
        <p>View and manage indexes</p>
      </div>

      <div className="filters-section">
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <FormControl sx={{ minWidth: 200, padding: '5px' }}>
            <InputLabel>Filter by Type</InputLabel>
            <Select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              label="Filter by Type"
              size="small"
            >
              <MenuItem value="">All Types</MenuItem>
              {indexTypes
                .map(type => type.name || type)
                .sort()
                .map(type => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {searchType && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSearchType('')}
              sx={{ height: 40 }}
            >
              Clear Filter
            </Button>
          )}
        </Box>
      </div>

      {loading ? (
        <Box display="flex" justifyContent="center" padding={4}>
          <CircularProgress />
        </Box>
      ) : (
        <div className="items-grid">
          {filteredIndexes.map((index) => (
            <Card key={index.id} className="admin-item-card">
              <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column', '&:last-child': { pb: 0 } }}>
                <Box sx={{ p: 2.5, flex: 1 }}>
                  {/* Header with name */}
                  <Typography variant="h6" component="div" sx={{ mb: 2 }}>
                    {index.name}
                  </Typography>

                  {/* ID and Type */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      <strong>ID:</strong> {index.id}
                    </Typography>
                    {index.type && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Type:</strong> {index.type}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Divider */}
                <Box sx={{ borderTop: '1px solid #eee' }} />

                {/* Bottom section - aligned to bottom */}
                <Box sx={{ p: 2.5, pt: 2 }}>
                  {/* Created date and user */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Created
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatDate(index.created_at)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        User
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {index.user || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color={index.approved ? 'warning' : 'success'}
                      startIcon={index.approved ? <CancelIcon /> : <CheckCircleIcon />}
                      onClick={() => handleApprovalToggle(index)}
                      sx={{ minWidth: 100 }}
                    >
                      {index.approved ? 'Disapprove' : 'Approve'}
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          if (!deleteLoading) {
            setDeleteDialogOpen(false);
            setSelectedIndex(null);
          }
        }}
        onConfirm={confirmDelete}
        itemName={selectedIndex?.name || 'this index'}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default IndexManagement;